import React from "react";
import { Link } from "react-router-dom";
import { LockKey, ArrowSquareOut, Coin } from "@phosphor-icons/react";
import { useAuth } from "../auth/AuthProvider";
import ConnectWalletButton from "./ConnectWalletButton";

/**
 * Wraps a Pro-only feature. If the user is Pro, renders {children}.
 * Otherwise renders a locked panel that explains the MFSMAI Pro requirement
 * and offers Connect Wallet + Buy on PancakeSwap CTAs.
 */
export default function ProGate({ children, feature = "this feature", className = "" }) {
  const { user, isPro } = useAuth();
  if (isPro) return children;

  const hasWallet = !!user?.wallet_address;
  const balance = Number(user?.pro_balance || 0);
  const needed = 5000;

  return (
    <div
      data-testid="pro-gate"
      className={`relative border border-edge/35 bg-zinc-950/70 p-6 sm:p-8 overflow-hidden ${className}`}
    >
      {/* subtle scanline / radial accent */}
      <div className="pointer-events-none absolute inset-0 opacity-50"
        style={{ background:
          "radial-gradient(800px 200px at 80% 0%, rgba(0,216,255,0.08), transparent 60%)"
        }}
      />
      <div className="relative flex items-start gap-4">
        <div className="shrink-0 w-12 h-12 border border-edge/50 flex items-center justify-center bg-edge/5">
          <LockKey size={22} weight="duotone" className="text-edge" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-edge mb-1">
            MFSMAI Pro · Token-gated
          </div>
          <h3 className="font-display text-xl sm:text-2xl font-semibold leading-tight">
            Hold 5,000 MFSMAI to unlock {feature}.
          </h3>
          <p className="mt-2 text-sm font-body text-zinc-300 leading-relaxed max-w-2xl">
            MFSMAI Pro is a no-subscription, token-gated tier. Hold at least
            <span className="text-edge font-semibold"> 5,000 MFSMAI</span> in any
            BSC wallet, sign once to verify ownership, and every Pro feature
            unlocks automatically. No payments, no recurring charges.
          </p>

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <ProStatCell label="Required" value="5,000 MFSMAI" />
            <ProStatCell
              label="Your balance"
              value={
                hasWallet
                  ? `${balance.toLocaleString(undefined, { maximumFractionDigits: 2 })} MFSMAI`
                  : "Wallet not linked"
              }
              accent={hasWallet ? (balance >= needed ? "text-profit" : "text-loss") : "text-zinc-400"}
            />
            <ProStatCell label="Chain" value="BNB Smart Chain · BEP-20" />
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <ConnectWalletButton variant="hero" />
            <a
              href="https://pancakeswap.finance/swap?outputCurrency=0x419daCaF319f616d322fC4bB5521A27ce51B8E55"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="buy-mfsmai-btn"
              className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 px-4 py-2.5 text-sm font-display uppercase tracking-[0.18em] text-zinc-200 hover:bg-white/5 transition-colors"
            >
              <Coin size={14} weight="fill" /> Buy MFSMAI on PancakeSwap
              <ArrowSquareOut size={11} />
            </a>
            <Link
              to="/about"
              data-testid="learn-more-link"
              className="text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-edge"
            >
              Learn about MFSMAI →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProStatCell({ label, value, accent }) {
  return (
    <div className="border border-white/10 p-3 bg-zinc-950/40">
      <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">{label}</div>
      <div className={`mt-1 font-mono text-sm tabular-nums ${accent || ""}`}>{value}</div>
    </div>
  );
}
